import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProductReviews } from "../../slices/reviews";
import PostReviewForm from "./PostReview";

export const styles = {
  reviewsContainer: {
    fontFamily: "Arial, sans-serif",
    padding: "20px",
    borderRadius: "8px",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  header: {
    textAlign: "center",
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "20px",
    color: "#333",
  },
  summarySection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "20px",
    paddingBottom: "20px",
    borderBottom: "1px solid #eee",
  },
  avgRatingBox: { textAlign: "center", padding: "10px" },
  stars: { color: "#ffc107", fontSize: "20px", marginBottom: "5px" },
  ratingText: { fontSize: "24px", fontWeight: "600", color: "#333" },
  basedOn: { fontSize: "14px", color: "#777" },
  breakdown: { flex: "1", minWidth: "250px", maxWidth: "400px" },
  ratingBarRow: { display: "flex", alignItems: "center", marginBottom: "8px" },
  starLabel: { width: "30px", textAlign: "right", color: "#333" },
  progressBarContainer: {
    flex: 1,
    height: "8px",
    backgroundColor: "#f0f0f0",
    borderRadius: "4px",
    margin: "0 10px",
  },
  progressBar: (w) => ({
    height: "100%",
    width: `${w}%`,
    backgroundColor: "#E53E3E",
    borderRadius: "4px",
  }),
  count: { color: "#777", minWidth: "10px" },
  writeReviewButton: {
    backgroundColor: "#E53E3E",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "25px",
    fontSize: "16px",
    cursor: "pointer",
    fontWeight: "500",
  },
  reviewList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "20px",
    marginTop: "30px",
  },
  reviewCard: {
    border: "1px solid #eee",
    borderRadius: "8px",
    padding: "20px",
    backgroundColor: "#fcfcfc",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },
  reviewerInfo: { display: "flex", alignItems: "center", marginTop: "10px" },
  avatar: {
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    backgroundColor: "#e0e0e0",
    marginRight: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: "bold",
    color: "#555",
  },
  userName: { fontWeight: "bold", color: "#333" },
  reviewTitle: { fontWeight: "600", marginTop: "8px", color: "#333" },
  reviewBody: { fontSize: "14px", color: "#555", marginTop: "6px" },
  reviewImageContainer: {
    marginTop: "12px",
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
  },
  reviewImage: {
    width: "60px",
    height: "60px",
    objectFit: "cover",
    borderRadius: "4px",
    border: "1px solid #eee",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "30px",
  },
  loadMoreButton: {
    backgroundColor: "#fff",
    color: "#E53E3E",
    border: "1px solid #E53E3E",
    padding: "8px 20px",
    borderRadius: "6px",
    fontSize: "15px",
    cursor: "pointer",
  },
  disabledButton: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
};

// helper
const getStarIcons = (rating) => {
  const fullStars = Math.floor(rating);
  const emptyStars = 5 - fullStars;
  return (
    <span style={styles.stars}>
      {"★".repeat(fullStars)}
      {"☆".repeat(emptyStars)}
    </span>
  );
};

const CustomerReviews = ({ productId }) => {
  const dispatch = useDispatch();
  const {
    reviews,
    total,
    totalPages,
    currentPage,
    loading,
  } = useSelector((state) => state.reviews);

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchProductReviews({ productId, page: 1 }));
  }, [dispatch, productId]);

  const averageRating =
    total > 0
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
      : 0;

  const ratingBreakdown = [5, 4, 3, 2, 1].map((star) => {
    const count = reviews.filter((r) => r.rating === star).length;
    return {
      stars: star,
      count,
      percentage: total ? (count / total) * 100 : 0,
    };
  });

  const handleLoadMore = () => {
    if (currentPage < totalPages && !loading) {
      dispatch(fetchProductReviews({ productId, page: currentPage + 1 }));
    }
  };

  return (
    <div style={styles.reviewsContainer}>
      <h2 style={styles.header}>Customer Reviews</h2>

      <div style={styles.summarySection}>
        <div style={styles.avgRatingBox}>
          <div>{getStarIcons(averageRating)}</div>
          <div style={styles.ratingText}>{averageRating} / 5</div>
          <div style={styles.basedOn}>{total} reviews</div>
        </div>

        <div style={styles.breakdown}>
          {ratingBreakdown.map((item) => (
            <div key={item.stars} style={styles.ratingBarRow}>
              <span style={styles.starLabel}>{item.stars}★</span>
              <div style={styles.progressBarContainer}>
                <div style={styles.progressBar(item.percentage)} />
              </div>
              <span style={styles.count}>{item.count}</span>
            </div>
          ))}
        </div>

        <button
          style={styles.writeReviewButton}
          onClick={() => setIsModalOpen(true)}
        >
          Write a Review
        </button>
      </div>

      <div style={styles.reviewList}>
        {loading && reviews.length === 0 && <p>Loading reviews...</p>}

        {!loading && reviews.length === 0 && (
          <p style={{ textAlign: "center", color: "#777" }}></p>
        )}

        {reviews.map((r) => (
          <div key={r.id} style={styles.reviewCard}>
            {getStarIcons(r.rating)}
            <div style={styles.reviewerInfo}>
              <div style={styles.avatar}>
                {(r.user?.first_name?.[0] || "") +
                  (r.user?.last_name?.[0] || "")}
              </div>
              <span style={styles.userName}>
                {r.user?.first_name} {r.user?.last_name}
              </span>
            </div>
            <p style={styles.reviewTitle}>{r.title}</p>
            <p style={styles.reviewBody}>{r.comment}</p>

            {r.images?.length > 0 && (
              <div style={styles.reviewImageContainer}>
                {r.images.map((img, i) => (
                  <img key={i} src={img} alt="" style={styles.reviewImage} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {currentPage < totalPages && (
        <div style={styles.paginationContainer}>
          <button
            style={{
              ...styles.loadMoreButton,
              ...(loading ? styles.disabledButton : {}),
            }}
            disabled={loading}
            onClick={handleLoadMore}
          >
            {loading ? "Loading..." : "Load More"}
          </button>
        </div>
      )}

      {isModalOpen && (
        <PostReviewForm
          productId={productId}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default CustomerReviews;
